import { useState, useRef } from "react";
import { Loader2, Check, Camera, Upload, FileText, Trash2, Image, Paperclip, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function DepensesFormDialog({ open, onClose, editItem, form, setForm, onSave, saving, config, classeurs, api }) {
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const applyOcr = (extracted) => {
    if (!extracted) return;
    setForm(prev => ({
      ...prev,
      element: extracted.element || prev.element,
      solde_ttc: extracted.montant_ttc || prev.solde_ttc,
      facture_ref: extracted.facture_ref || prev.facture_ref,
      category: extracted.category || prev.category,
      modalite_paiement: extracted.modalite_paiement && extracted.modalite_paiement !== "inconnu" ? extracted.modalite_paiement : prev.modalite_paiement,
      echeance: extracted.date || prev.echeance,
      notes: [prev.notes, extracted.societe ? `Creancier: ${extracted.societe}` : "", extracted.montant_ht ? `HT: ${extracted.montant_ht} EUR` : "", extracted.taux_tva ? `TVA: ${extracted.taux_tva}` : "", extracted.notes || ""].filter(Boolean).join(" | "),
    }));
    setOcrResult(extracted);
  };

  const handleFileUpload = async (file) => {
    if (!file || !api) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
    if (!allowed.includes(file.type)) {
      alert("Format non supporte. Acceptes: JPEG, PNG, WebP, HEIC, PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Fichier trop volumineux (max 10 Mo)");
      return;
    }

    // 1. Run OCR extraction
    setOcrLoading(true);
    try {
      const ocrFd = new FormData();
      ocrFd.append("file", file);
      const ocrResp = await api.post("/depenses/ocr-extract", ocrFd, { headers: { "Content-Type": "multipart/form-data" } });
      if (ocrResp.data?.extracted) {
        applyOcr(ocrResp.data.extracted);
      }
    } catch (e) {
      console.warn("OCR failed:", e.response?.data?.detail || e.message);
    }
    setOcrLoading(false);

    // 2. Upload as justificatif
    if (editItem?.depense_id) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("depense_id", editItem.depense_id);
        const r = await api.post("/depenses/upload-justificatif", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm(prev => ({ ...prev, justificatif: r.data.justificatif }));
      } catch (e) {
        alert(e.response?.data?.detail || "Erreur d'upload");
      }
      setUploading(false);
    } else {
      setForm(prev => ({ ...prev, _pendingFile: file, _pendingFileName: file.name }));
    }
  };

  const removeJustificatif = async () => {
    if (editItem?.depense_id && api && form.justificatif?.path) {
      try { await api.delete(`/depenses/justificatif/${editItem.depense_id}`); } catch {}
    }
    setForm(prev => ({ ...prev, justificatif: null, _pendingFile: null, _pendingFileName: null }));
    setOcrResult(null);
  };

  const hasJustificatif = form.justificatif?.url || form.justificatif?.path || form._pendingFile;
  const justificatifName = form.justificatif?.filename || form._pendingFileName;
  const isImage = form.justificatif?.content_type?.startsWith("image/") || form._pendingFile?.type?.startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setOcrResult(null); } }}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto mx-2">
        <DialogHeader>
          <DialogTitle className="text-[#f7c935]">{editItem ? "Modifier la depense" : "Nouvelle depense"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">

          {/* Justificatif / Receipt upload — FIRST */}
          <div className="bg-gradient-to-r from-blue-500/5 to-purple-500/5 border border-blue-500/10 rounded-lg p-3">
            <label className="text-xs mb-2 block font-bold flex items-center gap-1.5 text-blue-300">
              <Sparkles className="w-3.5 h-3.5" /> Justificatif avec OCR intelligent
            </label>
            <p className="text-[9px] text-slate-500 mb-2">Chargez une photo ou un PDF — les informations seront extraites automatiquement</p>
            {hasJustificatif ? (
              <div className="flex items-center gap-3 bg-[#0b0811] rounded-lg p-2.5 border border-white/10" data-testid="justificatif-preview">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {isImage ? <Image className="w-5 h-5 text-blue-400" /> : <FileText className="w-5 h-5 text-amber-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{justificatifName || "Justificatif"}</p>
                  {form.justificatif?.size && <p className="text-[9px] text-slate-500">{(form.justificatif.size / 1024).toFixed(0)} Ko</p>}
                  {form._pendingFile && <p className="text-[9px] text-amber-400">Sera enregistre a la sauvegarde</p>}
                  {ocrResult && <p className="text-[9px] text-green-400 flex items-center gap-1"><Check className="w-2.5 h-2.5" /> Donnees extraites par OCR</p>}
                </div>
                {form.justificatif?.url && (
                  <a href={form.justificatif.url} target="_blank" rel="noopener noreferrer"
                    className="p-1.5 hover:bg-white/5 rounded text-blue-400 text-[10px] flex-shrink-0" data-testid="view-justificatif">Voir</a>
                )}
                <button onClick={removeJustificatif} className="p-1.5 hover:bg-red-500/10 rounded flex-shrink-0" data-testid="remove-justificatif">
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            ) : ocrLoading ? (
              <div className="flex items-center gap-3 py-4 justify-center" data-testid="ocr-loading">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                <span className="text-xs text-blue-300">Extraction OCR en cours...</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2" data-testid="justificatif-upload-zone">
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="file-input"
                  onChange={e => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-input"
                  onChange={e => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }} />
                <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-blue-500/20 text-blue-300 text-xs h-9 flex-1 hover:bg-blue-500/10" data-testid="upload-file-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Charger fichier
                </Button>
                <Button onClick={() => cameraInputRef.current?.click()} variant="outline" className="border-purple-500/20 text-purple-300 text-xs h-9 flex-1 hover:bg-purple-500/10" data-testid="upload-camera-btn">
                  <Camera className="w-3.5 h-3.5 mr-1.5" /> Prendre photo
                </Button>
              </div>
            )}
          </div>

          {/* OCR result banner */}
          {ocrResult && ocrResult.confidence > 0 && (
            <div className="flex items-center gap-2 bg-green-500/5 border border-green-500/10 rounded-lg px-3 py-2" data-testid="ocr-result-banner">
              <Sparkles className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] text-green-400 font-bold">OCR : donnees extraites avec succes ({Math.round((ocrResult.confidence || 0) * 100)}% confiance)</p>
                <p className="text-[9px] text-slate-500">
                  {ocrResult.societe && `${ocrResult.societe} • `}
                  {ocrResult.montant_ttc > 0 && `${ocrResult.montant_ttc} EUR TTC • `}
                  {ocrResult.montant_ht > 0 && `${ocrResult.montant_ht} EUR HT • `}
                  {ocrResult.facture_ref && `Ref: ${ocrResult.facture_ref}`}
                </p>
              </div>
            </div>
          )}

          {/* Form fields */}
          <div>
            <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Element / Description *</label>
            <Input value={form.element} onChange={e => setForm({ ...form, element: e.target.value })} placeholder="Ex: Loyer bureau, Fiche de paie..." className="bg-[#0b0811] border-white/10 text-white" data-testid="form-element" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Categorie</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="form-category"
                className="w-full bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none">
                {(config.categories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Statut</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="form-status"
                className="w-full bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none">
                {(config.statuses || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Solde TTC (EUR)</label>
              <Input type="number" value={form.solde_ttc} onChange={e => setForm({ ...form, solde_ttc: e.target.value })} placeholder="0.00" className="bg-[#0b0811] border-white/10 text-white" data-testid="form-solde" />
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Echeance</label>
              <input type="date" value={form.echeance} onChange={e => setForm({ ...form, echeance: e.target.value })}
                className="w-full bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none"
                data-testid="form-echeance" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Modalite de paiement</label>
              <select value={form.modalite_paiement} onChange={e => setForm({ ...form, modalite_paiement: e.target.value })}
                className="w-full bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none" data-testid="form-modalite">
                <option value="">-</option>
                {["Virement", "Prelevement", "CB", "Cheque", "Especes", "LCR"].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Compte</label>
              <Input value={form.compte} onChange={e => setForm({ ...form, compte: e.target.value })} placeholder="Ex: BNP Pro" className="bg-[#0b0811] border-white/10 text-white" />
            </div>
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Ref. Facture</label>
            <Input value={form.facture_ref} onChange={e => setForm({ ...form, facture_ref: e.target.value })} placeholder="F-2026-001" className="bg-[#0b0811] border-white/10 text-white" />
          </div>
          <div>
            <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Classeur</label>
            <select value={form.classeur} onChange={e => setForm({ ...form, classeur: e.target.value })}
              className="w-full bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none" data-testid="form-classeur">
              <option value="">Aucun</option>
              {(classeurs || []).map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          {form.status === "appurement" && (
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-white/5">
              <div>
                <label className="text-xs mb-1 block text-pink-400">Montant mensuel appurement</label>
                <Input type="number" value={form.appurement_mensuel} onChange={e => setForm({ ...form, appurement_mensuel: e.target.value })} placeholder="0.00" className="bg-[#0b0811] border-white/10 text-white" data-testid="form-appurement-mensuel" />
              </div>
              <div>
                <label className="text-xs mb-1 block text-pink-400">Nb mois lissage</label>
                <Input type="number" value={form.appurement_nb_mois} onChange={e => setForm({ ...form, appurement_nb_mois: e.target.value })} placeholder="12" className="bg-[#0b0811] border-white/10 text-white" data-testid="form-appurement-mois" />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Notes</label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Remarques..." className="bg-[#0b0811] border-white/10 text-white" />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={() => { onClose(); setOcrResult(null); }} variant="outline" className="border-white/10 text-slate-400 h-9 text-xs">Annuler</Button>
            <Button onClick={onSave} disabled={saving || !form.element.trim()} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="save-depense-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {editItem ? "Mettre a jour" : "Ajouter"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
